import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Search } from 'lucide-react';
import { fmtCurrency } from '../../lib/localeUtils';
import { useCatalogLayout } from './useCatalogLayout';
import { isPOSAvailable } from '../../lib/posAvailability';

/** Category-based accent so the wall of cards has visual rhythm. */
const accentFor = (product) => {
  if (product.requires_prescription) return 'from-blue-500/10 to-blue-500/5 border-l-blue-500';
  const cat = (product.category || '').toLowerCase();
  if (cat.includes('antibiotic')) return 'from-rose-500/10 to-rose-500/5 border-l-rose-500';
  if (cat.includes('analgesic') || cat.includes('pain')) return 'from-amber-500/10 to-amber-500/5 border-l-amber-500';
  if (cat.includes('vitamin') || cat.includes('supplement')) return 'from-emerald-500/10 to-emerald-500/5 border-l-emerald-500';
  return 'from-slate-500/5 to-transparent border-l-slate-300';
};

const POSProductCard = ({ product, onAdd }) => {
  const live = (product.batches || []).filter((b) => (b.quantity || 0) > 0);
  const recalled = product.recall_locked || (live.length > 0 && live.every((b) => b.recalled));
  const oos = product.stock_quantity <= 0;
  const low = !oos && product.stock_quantity <= product.min_stock_level;
  return (
    <Card
      className={[
        'pos-product-card cursor-pointer transition-[box-shadow,transform] duration-200 border-l-4',
        'hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]',
        `bg-gradient-to-br ${accentFor(product)}`,
        oos || recalled ? 'opacity-50 saturate-50' : '',
      ].join(' ')}
      onClick={() => onAdd(product)}
      role="button" tabIndex={0} title={product.name}
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onAdd(product); } }}
      data-testid={`product-card-${product.product_id}`}
    >
      <CardContent className="pos-product-body">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="pos-product-name text-xs font-semibold leading-tight line-clamp-2" data-testid={`product-name-${product.product_id}`}>{product.name}</p>
            {product.generic_name && (
              <p className="pos-product-generic text-[11px] text-muted-foreground truncate mt-1 italic">{product.generic_name}</p>
            )}
          </div>
          {recalled && (
            <Badge variant="destructive" className="text-[10px] shrink-0 px-1.5" data-testid={`product-card-recalled-${product.product_id}`}>RECALLED</Badge>
          )}
          {product.requires_prescription && (
            <Badge variant="outline" className="text-[10px] shrink-0 px-1.5 border-blue-400 text-blue-600">Rx</Badge>
          )}
        </div>
        <div className="flex items-end justify-between gap-1 mt-auto">
          <span className="text-sm font-bold text-foreground tabular-nums break-all" data-testid={`product-price-${product.product_id}`}>{fmtCurrency(product.unit_price)}</span>
          <Badge
            variant={oos ? 'destructive' : low ? 'secondary' : 'outline'}
            className={['text-[10px] tabular-nums', low && !oos ? 'border-amber-400 text-amber-700 bg-amber-50' : ''].join(' ')}
            data-testid={`product-stock-${product.product_id}`}
          >
            {oos ? 'Out' : low ? `${product.stock_quantity} low` : `${product.stock_quantity}`}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

export const POSProductGrid = ({ products, loading, onAddToCart, searching }) => {
  const availableProducts = products.filter(product => isPOSAvailable(product));
  const layout = useCatalogLayout(loading ? 15 : availableProducts.length);
  if (loading) {
    return (
      <div ref={layout.ref} style={layout.style} className="pos-product-grid" data-testid="pos-catalog-loading">
        {Array.from({ length: 15 }, (_, i) => <div key={i} className="bg-muted rounded-lg animate-pulse" />)}
      </div>
    );
  }
  if (availableProducts.length === 0) {
    return (
      <div ref={layout.ref} className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="pos-no-products">
        <Search className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm">{searching ? 'No products found' : 'No frequently sold items yet'}</p>
      </div>
    );
  }
  return (
    <div ref={layout.ref} style={layout.style} data-compact={layout.compact} className="pos-product-grid" data-testid="pos-product-grid">
      {availableProducts.map((product) => (
        <POSProductCard key={product.product_id} product={product} onAdd={onAddToCart} />
      ))}
    </div>
  );
};
